import { Box, SxProps, Theme, Tooltip, TooltipProps } from '@mui/material'
import { C1ButtonColorType, C1IconButton } from 'c1ui'
import { C1Icon } from 'components/core/icons/C1Icon'
import { LucideIcon } from 'lucide-react'
import { FC } from 'react'

type BaseProps = {
    /** The Lucide icon component to render */
    Icon: LucideIcon
    /** Tooltip title text */
    title: TooltipProps['title']
    /** Accessible label for the button (required) */
    'aria-label': string
    /** Button size */
    size?: 'small' | 'medium'
    /** Click handler */
    onClick?: () => void
    /** Whether the button is disabled */
    disabled?: boolean
    /** Additional styles */
    sx?: SxProps<Theme>
    /** Optional props to pass to the Tooltip component */
    tooltipProps?: Omit<TooltipProps, 'title' | 'children'>
}

type OutlinedVariantProps = BaseProps & {
    variant: 'outlined'
    color?: C1ButtonColorType
}

type TextVariantProps = BaseProps & {
    variant?: 'text'
    color?: 'inherit'
}

type C1IconButtonWithTooltipProps = OutlinedVariantProps | TextVariantProps

/**
 * An icon button with a tooltip. Takes a LucideIcon directly and renders it
 * inside a C1Icon wrapper.
 *
 * @example
 * <C1IconButtonWithTooltip
 *     Icon={ArrowRight}
 *     title="Next"
 *     aria-label="Go to next"
 *     onClick={handleNext}
 * />
 */
export const C1IconButtonWithTooltip: FC<C1IconButtonWithTooltipProps> = ({
    Icon,
    title,
    tooltipProps,
    variant,
    color,
    ...buttonProps
}) => {
    const iconElement = <C1Icon icon={Icon} />

    return (
        <Tooltip title={title} {...tooltipProps}>
            <Box component="span" sx={{ display: 'inline-flex' }}>
                {variant === 'outlined' ? (
                    <C1IconButton variant="outlined" color={color as C1ButtonColorType} {...buttonProps}>
                        {iconElement}
                    </C1IconButton>
                ) : (
                    <C1IconButton variant="text" color={color as 'inherit'} {...buttonProps}>
                        {iconElement}
                    </C1IconButton>
                )}
            </Box>
        </Tooltip>
    )
}
